import { useEffect } from 'react';
import { Button, Checkbox, Dialog, Skeleton, Slider } from '@radix-ui/themes';
import { useNavigate } from 'react-router-dom';
import { isCurrentUser, isLoggedIn } from 'helpers/authentication';
import Analytics from 'ui/design-base/Analytics';
import { endorse, endorsements, unendorse } from 'store/endorsements/endorsementsSlice';
import { useAppDispatch, useAppSelector } from 'store/store';
import type { EndorsementType } from 'types/player';
import Container from './Container';

export const PlayerEndorsements: React.FC<{ playerId?: number; userId?: number | null }> = ({ playerId, userId }) => {
  const endorsementState = useAppSelector((state) => state.endorsements);
  const { loading } = endorsementState;
  const endorsementsArray = endorsementState.endorsements;
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (playerId) dispatch(endorsements(playerId));
  }, [playerId, dispatch]);

  const onClickFunction = (endorsement: EndorsementType) => {
    if (isLoggedIn()) {
      if (endorsement.endorsement_id) {
        //unendorse
        Analytics.sendEvent({
          category: 'Profile Interaction',
          action: 'Endorsement - Removed',
          label: 'Player Profile',
        });
        dispatch(unendorse({ fitPlayerId: playerId!, endorsementId: endorsement.endorsement_id }));
      } else {
        //endorse
        Analytics.sendEvent({
          category: 'Profile Interaction',
          action: 'Endorsement - Added',
          label: 'Player Profile',
        });
        dispatch(endorse({ fitPlayerId: playerId!, endorsementTypeId: endorsement.id }));
      }
    } else {
      //redirect to login
      navigate('/accedi');
    }
  };

  const itemClassName = 'flex gap-2 items-center';
  const endorsementNameClassName = 'font-bold min-w-32';

  const endorsementDialog = (
    <Dialog.Root>
      <Dialog.Trigger>
        <Button className='mt-4'>Vota punti di forza</Button>
      </Dialog.Trigger>

      <Dialog.Content maxWidth='450px'>
        <Dialog.Title>Vota punti di forza</Dialog.Title>
        <Dialog.Description size='2' mb='4' className='text-neutral-neutral-8'>
          Scegli uno o più punti di forza che caratterizzano questo giocatore
        </Dialog.Description>

        <div className='flex flex-row flex-wrap'>
          {endorsementsArray?.map((endorsement) => (
            <div key={endorsement.id} className='flex w-1/2 gap-3 py-1'>
              <Checkbox
                className='cursor-pointer'
                size={'3'}
                defaultChecked={!!endorsement.endorsement_id}
                onClick={() => onClickFunction(endorsement)}
              />
              {endorsement.name}
            </div>
          ))}
        </div>

        <div className='mt-5 flex w-full flex-1 justify-center gap-3'>
          <Dialog.Close className='flex-1'>
            <Button variant='soft' color='gray'>
              Annulla
            </Button>
          </Dialog.Close>
          <Dialog.Close className='flex-1'>
            <Button>Conferma</Button>
          </Dialog.Close>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );

  return (
    <Container title='Punti di Forza'>
      <div className='text-neutral-neutral-8'>Votati dagli utenti di TennisTalker</div>
      <div className='mt-4 flex flex-col gap-3'>
        {!loading
          ? endorsementsArray?.map((endorsement) => (
              <div key={endorsement.id} className={itemClassName}>
                <div className={endorsementNameClassName}>{endorsement.name}</div>
                <Slider value={[endorsement.points]} size={'3'} variant='soft' className='rank-slider' />
              </div>
            ))
          : Array(6)
              .fill(0)
              .map((_, i) => (
                <Skeleton key={i} className='h-5 w-full'>
                  <div className={itemClassName}>
                    <div className={endorsementNameClassName}>Dritto</div>
                    <Slider size={'3'} variant='soft' className='rank-slider' />
                  </div>
                </Skeleton>
              ))}
      </div>
      {!isCurrentUser(userId) && endorsementDialog}
    </Container>
  );
};

export default PlayerEndorsements;
